import type { DebtData } from '@/types';
import type { OphelosPaymentPlanResponse } from '@/types/payment-plan';
import { activeAccountPayment, scheduledAccountPaymentAmount } from './paymentPlans';

/** Id of the synthetic entry below. Not a case id — resolving a debt by id must special-case it. */
export const ACCOUNT_ENTRY_ID = 'account-payment';

/** Where an active account payment is shown, and can be cancelled. */
export const accountPaymentPath = (payment: 'scheduled' | 'plan'): string =>
  payment === 'plan' ? `/payment-plan/${ACCOUNT_ENTRY_ID}` : '/account-payment';

/** Whether the active account payment covers this debt, which is then grouped and opened with it. */
export const coveredByAccountPayment = (debt: DebtData, payment: 'scheduled' | 'plan' | null): payment is 'scheduled' | 'plan' =>
  payment != null && !!debt.isAccountPlanMember;

/**
 * The currency to render an account-wide total in. Transactions carry none of their own, and an
 * account is scoped to one market, so any of its debts answers for all of them.
 */
export function accountCurrency(debts: DebtData[]): string {
  return debts.find((debt) => debt.currency)?.currency ?? 'EUR';
}

/**
 * Debt label per transaction, for a list spanning several debts — FIFO can land an instalment
 * on any covered case, so the row must say which. A case's own history needs none.
 */
export function debtNameByTransaction(debts: DebtData[]): Map<string, string> {
  // The case reference the dashboard card shows — both surfaces must name a debt the same way.
  return new Map(debts.flatMap((debt) => (debt.transactions ?? []).map((tx) => [tx.transaction_id, debt.description] as const)));
}

/**
 * The synthetic debt standing for the account's active account payment — a scheduled
 * payment or a payment plan — or null when there is none. Shaped as an ordinary
 * `DebtData` so the dashboard card and plan detail page need no account-specific
 * code.
 */
export function buildAccountEntry(
  accountPaymentPlan: OphelosPaymentPlanResponse | null,
  debts: DebtData[],
  coversLabel: (count: number) => string,
): DebtData | null {
  const variant = activeAccountPayment(accountPaymentPlan);
  if (!variant) return null;
  const members = debts.filter((d) => d.isAccountPlanMember);
  const template = members[0] ?? debts[0];
  const schedule = accountPaymentPlan!.schedule ?? [];
  const scheduled = variant === 'scheduled';
  const sum = (pick: (d: DebtData) => number) => members.reduce((total, d) => total + pick(d), 0);
  // `@/types` exports a same-named payment breakdown that carries `vat` — take the shape off DebtData.
  type Breakdown = DebtData['originalBreakdown'];
  const sumBreakdown = (pick: (d: DebtData) => Breakdown | undefined): Breakdown => ({
    principal: sum((d) => pick(d)?.principal ?? 0),
    interest: sum((d) => pick(d)?.interest ?? 0),
    fees: sum((d) => pick(d)?.fees ?? 0),
    total: sum((d) => pick(d)?.total ?? 0),
  });

  return {
    ...(template ?? ({} as DebtData)),
    id: ACCOUNT_ENTRY_ID,
    // No case owns an account plan, so there is no case ref to show for it.
    externalCaseRef: null,
    isAccountPlanMember: false,
    description: coversLabel(members.length),
    clientId: template?.clientId ?? null,
    originalCreditor: template?.originalCreditor ?? '',
    currency: template?.currency ?? 'EUR',
    // A scheduled payment is one fixed charge; a plan tracks what its cases owe.
    currentBalance: scheduled ? (scheduledAccountPaymentAmount(accountPaymentPlan) ?? 0) : sum((d) => d.currentBalance),
    originalAmount: scheduled ? (template?.originalAmount ?? 0) : sum((d) => d.originalAmount),
    // The breakdown tab is per component, and each component is owed across every covered case.
    originalBreakdown: sumBreakdown((d) => d.originalBreakdown),
    discountApplied: members.reduce((sum, d) => sum + d.discountApplied, 0),
    outstandingBreakdown: sumBreakdown((d) => d.outstandingBreakdown),
    adjustmentBreakdown: sumBreakdown((d) => d.adjustmentBreakdown),
    paymentBreakdown: sumBreakdown((d) => d.paymentBreakdown),
    // FIFO can land an installment on any member case. Kept whole — the breakdown tab reads this
    // for charges; scoping it to the plan is the transaction list's job, not this entry's.
    transactions: members.flatMap((d) => d.transactions ?? []),
    journeyNode: 'PAYING',
    status: 'in-progress',
    paymentPlan: {
      externalPaymentPlanId: accountPaymentPlan!.id,
      status: accountPaymentPlan!.status,
      totalPayments: scheduled ? 1 : schedule.length,
      completedPayments: scheduled ? 0 : schedule.filter((i) => i.status === 'succeeded').length,
      fullPlan: accountPaymentPlan!,
    },
  };
}
